import * as React from 'react';

import { Checkbox, Col, SearchField, cn } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideBuilding, LucideMapPin, LucideShield } from 'lucide-react';

import { useFacilitiesQuery } from './_graphql/useFacilitiesQuery';
import { useUserRoleAssignMutation } from './_graphql/useUserRoleAssignMutation';
import { useUserRoleRevokeMutation } from './_graphql/useUserRoleRevokeMutation';
import { useInitUserRoleFormFromApi } from './hooks/useInitUserRoleFormFromApi';

import {
  DetailPanel,
  EmptyDetailState,
  FormFooter,
  SelectionListItem,
  SettingsContent,
  TwoPanelSelectionLayout,
} from '~/modules/settings/_components/_shared';
import {
  $discardRoleDraftChanges,
  $selectRole,
  $updateDraftFacilities,
  $userId,
  $userRoleForm,
  type UserRoleAssignment,
} from '~/modules/settings/_state/userForm.state';
import { useTranslations } from '~/modules/settings/i18n/useTranslations';

// =============================================================================
// Types
// =============================================================================

interface RoleListItem extends UserRoleAssignment {
  hasFacilities: boolean;
}

interface FacilityItem {
  id: string;
  name: string;
  address: string;
}

// =============================================================================
// Facility Checkbox Row Component
// =============================================================================

interface FacilityCheckboxRowProps {
  facility: FacilityItem;
  isChecked: boolean;
  onToggle: (checked: boolean) => void;
  showDivider?: boolean;
}

const FacilityCheckboxRow: React.FC<FacilityCheckboxRowProps> = ({
  facility,
  isChecked,
  onToggle,
  showDivider = true,
}) => {
  const t = useTranslations();

  return (
    <>
      {showDivider && <div className="border-t border-neutral/10" />}
      <div className="flex h-8 w-full items-center gap-2">
        <Checkbox
          isSelected={isChecked}
          onChange={onToggle}
          aria-label={t.teamAndPermissions.userDetails.roles.assignFacilityAriaLabel(
            facility.name,
          )}
          className="w-auto"
        />
        <span className="w-[257px] shrink-0 truncate text-sm text-neutral">
          {facility.name}
        </span>
        <div className="flex flex-1 items-center gap-1.5 text-neutral">
          <LucideMapPin size={16} className="shrink-0" />
          <span className="truncate text-sm">{facility.address}</span>
        </div>
      </div>
    </>
  );
};

// =============================================================================
// Facility Checkbox List Component
// =============================================================================

interface FacilityCheckboxListProps {
  facilities: FacilityItem[];
  selectedFacilityIds: string[];
  onFacilityToggle: (facilityId: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
}

const FacilityCheckboxList: React.FC<FacilityCheckboxListProps> = ({
  facilities,
  selectedFacilityIds,
  onFacilityToggle,
  onToggleAll,
}) => {
  const t = useTranslations();
  const [searchValue, setSearchValue] = React.useState('');

  const filteredFacilities = React.useMemo(
    () =>
      facilities.filter(
        (fac) =>
          fac.name.toLowerCase().includes(searchValue.toLowerCase()) ||
          fac.address.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [facilities, searchValue],
  );

  const allFacilitiesState = React.useMemo(() => {
    const selectedCount = filteredFacilities.filter((fac) =>
      selectedFacilityIds.includes(fac.id),
    ).length;

    if (selectedCount === 0) return 'unchecked';
    if (selectedCount === filteredFacilities.length) return 'checked';
    return 'indeterminate';
  }, [filteredFacilities, selectedFacilityIds]);

  const handleToggleAll = () => {
    onToggleAll(allFacilitiesState !== 'checked');
  };

  return (
    <Col gap3 className="@FacilityCheckboxList w-full">
      <div className="w-[250px]">
        <SearchField
          value={searchValue}
          onChange={setSearchValue}
          placeholder={t.teamAndPermissions.search.placeholder}
          aria-label={
            t.teamAndPermissions.userDetails.roles.searchFacilitiesAriaLabel
          }
          className="h-8"
        />
      </div>

      <Col className="w-full">
        <div className="flex h-8 w-full items-center gap-2">
          <Checkbox
            isSelected={allFacilitiesState === 'checked'}
            isIndeterminate={allFacilitiesState === 'indeterminate'}
            onChange={handleToggleAll}
            aria-label={
              t.teamAndPermissions.userDetails.roles
                .selectAllFacilitiesAriaLabel
            }
            className="w-auto"
          />
          <span className="w-[257px] shrink-0 text-sm font-semibold text-neutral">
            {t.teamAndPermissions.userDetails.roles.allFacilities(
              filteredFacilities.length,
            )}
          </span>
        </div>

        {filteredFacilities.map((facility) => (
          <FacilityCheckboxRow
            key={facility.id}
            facility={facility}
            isChecked={selectedFacilityIds.includes(facility.id)}
            onToggle={(checked) => onFacilityToggle(facility.id, checked)}
          />
        ))}
      </Col>
    </Col>
  );
};

// =============================================================================
// Role Detail Panel Component
// =============================================================================

interface RoleDetailPanelProps {
  role: RoleListItem;
  facilities: FacilityItem[];
  draftFacilityIds: string[];
  onFacilityToggle: (facilityId: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  onDiscard: () => void;
  onSave: () => void;
  isDirty: boolean;
  isSaving: boolean;
}

const RoleDetailPanel: React.FC<RoleDetailPanelProps> = ({
  role,
  facilities,
  draftFacilityIds,
  onFacilityToggle,
  onToggleAll,
  onDiscard,
  onSave,
  isDirty,
  isSaving,
}) => {
  const t = useTranslations();
  const lastUpdatedText = role.lastUpdatedAt
    ? t.teamAndPermissions.userDetails.roles.lastUpdated(
        new Date(role.lastUpdatedAt).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        role.lastUpdatedBy ?? 'Unknown',
      )
    : null;

  return (
    <DetailPanel
      header={
        <div className="px-8 pt-5">
          <Col className="gap-0.5">
            <span className="text-lg font-bold text-neutral">
              {role.roleName}
            </span>
            <span className="text-sm text-neutral/60">
              {lastUpdatedText ?? '\u00A0'}
            </span>
          </Col>
        </div>
      }
      footer={
        <FormFooter
          onDiscard={onDiscard}
          onSave={onSave}
          isDiscardDisabled={isSaving || !isDirty}
          isSaveDisabled={isSaving || !isDirty}
        />
      }
      className="@RoleDetailPanel"
    >
      <FacilityCheckboxList
        facilities={facilities}
        selectedFacilityIds={draftFacilityIds}
        onFacilityToggle={onFacilityToggle}
        onToggleAll={onToggleAll}
      />
    </DetailPanel>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const UserRolesTab = () => {
  const t = useTranslations();
  const toast = useToast();
  const userId = useAtomValue($userId);
  const roleForm = useAtomValue($userRoleForm);
  const selectRole = useSetAtom($selectRole);
  const updateDraftFacilities = useSetAtom($updateDraftFacilities);
  const discardRoleDraftChanges = useSetAtom($discardRoleDraftChanges);

  useInitUserRoleFormFromApi(userId);

  const [searchValue, setSearchValue] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);

  const { data: { facilities = [] } = {} } = useFacilitiesQuery({
    limit: 1000,
    offset: 0,
  });

  const assignMutation = useUserRoleAssignMutation();
  const revokeMutation = useUserRoleRevokeMutation();

  const facilityItems: FacilityItem[] = React.useMemo(
    () =>
      facilities.map((fac) => ({
        id: fac.id,
        name: fac.name,
        address: fac.address,
      })),
    [facilities],
  );

  const roleListItems: RoleListItem[] = React.useMemo(
    () =>
      roleForm.assignments.map((assignment) => ({
        ...assignment,
        hasFacilities: assignment.assignedFacilityIds.length > 0,
      })),
    [roleForm.assignments],
  );

  const filteredRoles = React.useMemo(
    () =>
      roleListItems.filter((role) =>
        role.roleName.toLowerCase().includes(searchValue.toLowerCase()),
      ),
    [roleListItems, searchValue],
  );

  const handleSelectRole = (roleId: string) => {
    if (roleForm.isDirty) {
      discardRoleDraftChanges();
    }
    selectRole(roleId);
  };

  const handleFacilityToggle = (facilityId: string, checked: boolean) => {
    if (checked) {
      updateDraftFacilities([...roleForm.draftFacilityIds, facilityId]);
    } else {
      updateDraftFacilities(
        roleForm.draftFacilityIds.filter((id) => id !== facilityId),
      );
    }
  };

  const handleToggleAll = (checked: boolean) => {
    if (checked) {
      updateDraftFacilities(facilityItems.map((fac) => fac.id));
    } else {
      updateDraftFacilities([]);
    }
  };

  const handleSave = async () => {
    const selectedRoleId = roleForm.selectedRoleId;
    if (!selectedRoleId) return;

    const assignment = roleForm.assignments.find(
      (a) => a.roleId === selectedRoleId,
    );
    const originalFacilityIds = assignment?.assignedFacilityIds ?? [];
    const draftFacilityIds = roleForm.draftFacilityIds;

    const added = draftFacilityIds.filter(
      (id) => !originalFacilityIds.includes(id),
    );
    const removed = originalFacilityIds.filter(
      (id) => !draftFacilityIds.includes(id),
    );

    setIsSaving(true);
    try {
      const promises: Promise<unknown>[] = [];

      for (const facilityId of added) {
        promises.push(
          assignMutation.mutateAsync({
            userId,
            roleId: selectedRoleId,
            facilityId,
          }),
        );
      }

      for (const facilityId of removed) {
        const raw = roleForm.rawAssignments.find(
          (r) => r.roleId === selectedRoleId && r.facilityId === facilityId,
        );
        if (raw) {
          promises.push(revokeMutation.mutateAsync({ assignmentId: raw.id }));
        }
      }

      await Promise.all(promises);
      toast.success(
        t.teamAndPermissions.userDetails.roles.messages.updateSuccess,
      );
    } catch (error) {
      console.error('Failed to update role facilities:', error);
      toast.error(t.teamAndPermissions.userDetails.roles.messages.updateError);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    discardRoleDraftChanges();
  };

  return (
    <SettingsContent size="full" className="@UserRolesTab h-full">
      <TwoPanelSelectionLayout
        items={filteredRoles}
        selectedId={roleForm.selectedRoleId}
        onSelect={handleSelectRole}
        getItemId={(role) => role.roleId}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        searchPlaceholder={t.teamAndPermissions.search.placeholder}
        leftPanelWidth={240}
        renderItem={(role, isSelected) => {
          const hasNoFacilities = role.assignedFacilityIds.length === 0;

          return (
            <div className="px-2">
              <SelectionListItem
                name={role.roleName}
                tagIcon={<LucideBuilding size={14} />}
                tagCount={role.assignedFacilityIds.length}
                isSelected={isSelected}
                className={cn(hasNoFacilities && !isSelected && 'opacity-60')}
              />
            </div>
          );
        }}
        renderDetail={(role) =>
          role ? (
            <RoleDetailPanel
              role={role}
              facilities={facilityItems}
              draftFacilityIds={roleForm.draftFacilityIds}
              onFacilityToggle={handleFacilityToggle}
              onToggleAll={handleToggleAll}
              onDiscard={handleDiscard}
              onSave={handleSave}
              isDirty={roleForm.isDirty}
              isSaving={isSaving}
            />
          ) : (
            <EmptyDetailState
              icon={<LucideShield />}
              title={t.teamAndPermissions.userDetails.roles.emptyState.title}
              subtitle={
                t.teamAndPermissions.userDetails.roles.emptyState.subtitle
              }
            />
          )
        }
      />
    </SettingsContent>
  );
};
